import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import * as Yup from 'yup';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  FormLabel,
  TablePagination,
  TextField,
} from '@mui/material';

import {
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  colors,
  Tooltip,
} from '@material-ui/core';

import {
  AddCircle,
  Edit,
  Search,
  CloudDownload as Export,
  CloudDownload,
} from '@material-ui/icons';

import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import { useStyles, StyledTableCell } from './styles';
import { CreatePatientData } from './dto';
import Loader from '../../../components/Loader';
import Input from '../../../components/Input';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';

import { useAxios } from '../../../utils/hooks';
import getValidationErrors from '../../../utils/getValidationErrors';

import { Patient } from '../../../models/patient';
import Select from '../../../components/Select';

const Patients: React.FC = () => {
  const classes = useStyles();
  const api = useAxios();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [document, setDocument] = useState('');
  const [name, setName] = useState('');
  const [link, setLink] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [editPatient, setEditPatient] = useState<Patient>({} as Patient);
  const [openEditPatient, setOpenEditPatient] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openPatient, setOpenPatient] = useState(false);
  const formRef = useRef<FormHandles>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(4);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function getPatients() {
      try {
        const patientsResponse = await api.current?.get('/patients', {
          params: filters,
        });

        setPatients(patientsResponse?.data);
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar pacientes.',
        });
      } finally {
        setLoading(false);
      }
    }

    getPatients();
  }, [api, filters]);

  const handleOpenPatient = () => {
    setOpenPatient(true);
  };

  const handleClosePatient = () => {
    setOpenPatient(false);
  };

  const handleCloseEditPatient = () => {
    setEditPatient({} as Patient);
    setOpenEditPatient(false);
  };

  const handleSearchPatient = useCallback(() => {
    if (!document && !name) {
      setFilters({});
      return;
    }

    const aux = {} as any;

    if (document) aux.document = document;
    if (name) aux.name = name;

    setFilters(aux);
  }, [document, name]);

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleSubmitPatient = useCallback(
    async (data: CreatePatientData) => {
      try {
        resetAlert();
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string().required('Nome é obrigatório'),
          document: Yup.string().required('Documento é obrigatório'),
          document_type: Yup.string()
            .required('Tipo do documento é obrigatório')
            .oneOf(['cpf', 'rg', 'certidao'], 'Preencha o tipo do documento'),
          email: Yup.string().email(),
          birth: Yup.string(),
        });

        await schema.validate(data, { abortEarly: false });
        if (!data.email) delete data.email;
        if (!data.birth) delete data.birth;

        const newPatient = await api.current?.post(`/patients`, data);

        if (newPatient?.data) setPatients([newPatient?.data, ...patients]);

        setAlert({
          isActive: true,
          type: 'success',
          message: 'Paciente criado com sucesso.',
        });
        handleClosePatient();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao criar paciente.',
          });
        }
      }
    },
    [api, patients],
  );

  const handleSubmitEditPatient = useCallback(
    async (data: CreatePatientData) => {
      try {
        resetAlert();
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string().required('Nome é obrigatório'),
          email: Yup.string().email(),
          birth: Yup.string(),
        });

        await schema.validate(data, { abortEarly: false });
        if (!data.email) delete data.email;
        if (!data.birth) delete data.birth;

        await api.current?.patch(`/patients/${editPatient._id}`, data);
        const currentPatients = patients;
        const patientIdx = currentPatients.findIndex(
          (patient) => patient._id === editPatient._id,
        );
        currentPatients[patientIdx] = data as Patient;
        setPatients(currentPatients);

        setAlert({
          isActive: true,
          type: 'success',
          message: 'Paciente atualizado com sucesso.',
        });
        handleCloseEditPatient();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao atualizar paciente.',
          });
        }
      }
    },
    [api, editPatient, patients],
  );

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleOpenEditPatient = useCallback(
    async (id: string) => {
      try {
        const patientResponse = await api.current?.get(`/patients/${id}`);

        setEditPatient(patientResponse?.data);
        setOpenEditPatient(true);
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao buscar paciente.',
        });
      }
    },
    [api],
  );

  const handleLinkClose = () => {
    setLinkOpen(false);
    setLink('');
  };

  const handleClickLinkOpen = useCallback(async () => {
    resetAlert();
    setLoading(true);
    setLink('--');

    try {
      const linkResponse = await api.current?.get('/patients/search/print');

      if (linkResponse?.data && linkResponse?.data.link) {
        setLink(linkResponse?.data.link);
      } else {
        throw new Error();
      }

      setAlert({
        isActive: true,
        type: 'success',
        message: 'Arquivo gerado com sucesso.',
      });

      setLinkOpen(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);

      setAlert({
        isActive: true,
        type: 'error',
        message: 'Erro ao gerar arquivo.',
      });
    }
  }, [api]);

  return (
    <>
      <Loader loading={loading} />

      <Dialog
        open={openPatient}
        onClose={handleClosePatient}
        aria-labelledby="form-dialog-title"
      >
        <Form ref={formRef} onSubmit={handleSubmitPatient}>
          <DialogTitle id="form-dialog-title">Cadastrar Paciente</DialogTitle>

          <DialogContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Insira os dados do paciente
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input id="name" label="Nome" name="name" />
              </Grid>

              <Grid item xs={8}>
                <Input id="document" label="Documento" name="document" />
              </Grid>

              <Grid item xs={4}>
                <Select
                  name="document_type"
                  isMulti={false}
                  placeholder="Tipo"
                  isClearable
                  menuPosition="fixed"
                  options={[
                    { value: 'rg', label: 'RG' },
                    { value: 'cpf', label: 'CPF' },
                    // { value: 'certidao', label: 'Certidão de Nascimento' },
                  ]}
                />
              </Grid>

              <Grid item xs={6}>
                <Input id="email" label="E-mail (opcional)" name="email" />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="birth"
                  label="Data de Nascimento (opcional)"
                  name="birth"
                  placeholder="2021-01-01"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePatient}>Cancelar</Button>
            <Button type="submit" color="primary" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </Form>
      </Dialog>

      <Dialog
        open={linkOpen}
        fullWidth
        onClose={handleLinkClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          Arquivo da Pesquisa gerado
        </DialogTitle>

        <DialogContent>
          <Link href={link} target="blank">
            <Button
              fullWidth
              style={{ background: colors.green[700], color: '#fff' }}
              startIcon={<CloudDownload />}
              onClick={() => console.log('Baixar arquivo')}
            >
              Baixar arquivo
            </Button>
          </Link>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleLinkClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditPatient}
        onClose={handleCloseEditPatient}
        aria-labelledby="form-dialog-title"
      >
        <Form
          ref={formRef}
          initialData={editPatient}
          onSubmit={handleSubmitEditPatient}
        >
          <DialogTitle id="form-dialog-title">Editar Paciente</DialogTitle>

          <DialogContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Insira os dados do paciente
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input id="name" label="Nome" name="name" />
              </Grid>

              <Grid item xs={8}>
                <Input id="document" label="Documento" name="document" />
              </Grid>

              <Grid item xs={4}>
                <Select
                  name="document_type"
                  isMulti={false}
                  placeholder="Tipo"
                  isClearable
                  menuPosition="fixed"
                  options={[
                    { value: 'rg', label: 'RG' },
                    { value: 'cpf', label: 'CPF' },
                    // { value: 'certidao', label: 'Certidão de Nascimento' },
                  ]}
                />
              </Grid>

              <Grid item xs={6}>
                <Input id="email" label="E-mail (opcional)" name="email" />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="birth"
                  label="Data de Nascimento (opcional)"
                  name="birth"
                  placeholder="23/02/1997"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditPatient}>Cancelar</Button>
            <Button type="submit" color="primary" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </Form>
      </Dialog>

      <div className={classes.header}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircle />}
          className={classes.addButton}
          onClick={() => handleOpenPatient()}
        >
          Adicionar Paciente
        </Button>

        <Tooltip title="Temporariamente Indisponível">
          <span className={classes.button}>
            <Button
              variant="contained"
              startIcon={<Export />}
              className={classes.addButton}
              onClick={() => {
                handleClickLinkOpen();
              }}
              disabled
            >
              Exportar Pesquisa
            </Button>
          </span>
        </Tooltip>
      </div>

      <Paper elevation={0} className={classes.filterCard}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <span className={classes.filterLabel}>Documento</span>
            <TextField
              size="small"
              fullWidth
              id="search_document"
              placeholder="Digite um documento"
              onChange={(e) => setDocument(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <span className={classes.filterLabel}>Nome</span>
            <TextField
              size="small"
              fullWidth
              id="search_name"
              placeholder="Digite um nome"
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Button
              color="primary"
              variant="contained"
              startIcon={<Search />}
              className={classes.addButton}
              onClick={() => handleSearchPatient()}
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer
        component={Paper}
        style={{ display: !loading ? '' : 'none', marginBottom: '2%' }}
      >
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Nome</StyledTableCell>
              <StyledTableCell>Prontuário</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Documento</StyledTableCell>
              <StyledTableCell>Data de Nascimento</StyledTableCell>
              <StyledTableCell>Data de Cadastro</StyledTableCell>
              <StyledTableCell>Ultima Atualização</StyledTableCell>
              <StyledTableCell>Editar</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!patients.length && (
              <TableRow>
                <TableCell component="th" scope="row" colSpan={7}>
                  <Typography>Nenhum paciente encontrado.</Typography>
                </TableCell>
              </TableRow>
            )}

            {(rowsPerPage > 0
              ? patients.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage,
                )
              : patients
            ).map((patient) => (
              <TableRow key={patient._id}>
                <TableCell component="th" scope="row">
                  {patient.name}
                </TableCell>
                <TableCell component="th" scope="row">
                  {patient.recordNumber || '--'}
                </TableCell>
                <TableCell>{`${patient.email || '--'}`}</TableCell>
                <TableCell>
                  {`${patient.document || '--'} (${
                    patient.document_type
                      ? patient.document_type.toUpperCase()
                      : '--'
                  })`}
                </TableCell>
                <TableCell>
                  {!patient.birth
                    ? '--'
                    : moment(patient.birth).add(1, 'day').format('DD/MM/YYYY')}
                </TableCell>
                <TableCell>
                  {moment(patient.createdAt).format('DD/MM/YYYY HH:mm')}
                </TableCell>
                <TableCell>
                  {moment(patient.updatedAt).format('DD/MM/YYYY HH:mm')}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    onClick={() => {
                      handleOpenEditPatient(patient._id);
                    }}
                    color="primary"
                  >
                    <Edit />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 100]}
          component="div"
          count={patients.length}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage={<span>Pacientes por página:</span>}
          labelDisplayedRows={({ count, from, to }) => {
            return `${from} - ${to} de ${count}`;
          }}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default Patients;
